import { useMutation } from '@tanstack/react-query';
import { AuthenticatorType, DirectoryType, LogsFilters, ProtocolType } from '../../types';
import { LogEntry } from '../../types';
import { apiPost } from '../../../../api/mutations/apiPost';
import jstz from 'jstimezonedetect';
import { POLICY_ACTION_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import { getLogsRangeFilter } from '../../utils';

const END_POINT = '/Logs/all';

type LogsResponse = {
    Logs: LogEntryDto[];
    LogsCount: number;
    defaultDestinations: Record<string, string>;
    WallNames: Record<string, string>;
    hiddenRiskIndicators: string[];
};

type LogEntryDto = {
    user_identifier: string;
    user_type: number;
    user_identifier_display_name: string;
    host_name: string;
    protocol_type: ProtocolType;
    server: string;
    server_domain: string;
    service: string;
    user_guid: string;
    effective_rule_action: string | null;
    effective_rule_id: string | number | null;
    effective_rule_type: string;
    risk_indicators_v2: string[];
    token_id: string;
    authenticator: AuthenticatorType;
    directory_type: DirectoryType;
    server_guid: string | null;
    timestamp: number;
    action: boolean;
    user_domain: string;
    server_ip: string;
    external_user_id: string | null;
    host_guid: string | null;
    site: string | null;
    risk_level: number;
    company_id: number;
    domain_controller: string;
    user_response_interval: number | null;
    wall_id: string;
    tenant_id: string | number | null;
    request_id: string;
    is_ntlm_v1: boolean | null;
    service_identifier: string;
    upn: string;
    sam: string;
    auth_mode: string;
    vendor_id: number;
    user_response: string;
    ldap_params: Record<string, unknown>;
    is_internal_auth: boolean;
    id: string;
    time_stamp: number;
    reason: string;
    error_code: string;
};

type ReturnType = {
    logs: LogEntry[];
    logsCount: number;
    defaultDestinations: Record<string, string>;
    wallNames: Record<string, string>;
    hiddenRiskIndicators: string[];
};

const convertLogEntryDtoToLogEntry = (dto: LogEntryDto): LogEntry => ({
    userIdentifier: dto.user_identifier,
    userType: dto.user_type,
    userIdentifierDisplayName: dto.user_identifier_display_name,
    hostName: dto.host_name,
    protocolType: dto.protocol_type,
    server: dto.server,
    serverDomain: dto.server_domain,
    service: dto.service,
    userGuid: dto.user_guid,
    effectiveRuleAction: dto.effective_rule_action,
    effectiveRuleId: dto.effective_rule_id,
    effectiveRuleType: dto.effective_rule_type,
    riskIndicatorsV2: dto.risk_indicators_v2,
    tokenId: dto.token_id,
    authenticator: dto.authenticator,
    directoryType: dto.directory_type,
    serverGuid: dto.server_guid,
    timestamp: dto.timestamp,
    action: dto.action,
    userDomain: dto.user_domain,
    serverIp: dto.server_ip,
    externalUserId: dto.external_user_id,
    hostGuid: dto.host_guid,
    site: dto.site,
    riskLevel: dto.risk_level,
    companyId: dto.company_id,
    domainController: dto.domain_controller,
    userResponseInterval: dto.user_response_interval,
    wallId: dto.wall_id,
    tenantId: dto.tenant_id,
    requestId: dto.request_id,
    isNtlmV1: dto.is_ntlm_v1,
    serviceIdentifier: dto.service_identifier,
    upn: dto.upn,
    sam: dto.sam,
    authMode: dto.auth_mode,
    vendorId: dto.vendor_id,
    userResponse: dto.user_response,
    ldapParams: dto.ldap_params,
    isInternalAuth: dto.is_internal_auth,
    id: dto.id,
    timeStamp: dto.time_stamp,
    reason: dto.reason,
    errorCode: dto.error_code,
    ...(dto.effective_rule_action && { policyType: POLICY_ACTION_TYPES[dto.effective_rule_action.toUpperCase()].value })
});

const convertLogEntryDtosToLogEntries = (dtos: LogEntryDto[]): LogEntry[] => dtos.map(convertLogEntryDtoToLogEntry);

const buildLogsPayload = (filters: LogsFilters, excludedFilters: (keyof LogsFilters)[], maxEndDate?: Date) => {
    const Payload = {
        ExcludedFilters: excludedFilters,
        ForExport: false,
        LastIdentity: null,
        Filters: {} as Record<string, string[] | number[]>,
        LoadInitialData: true,
        localTimeZone: encodeURIComponent(jstz.determine().name())
    };
    if (filters.domain) {
        Payload.Filters.resourceDomain = filters.domain;
    }
    if (filters.timeframe) {
        Payload.Filters.range = getLogsRangeFilter(filters.timeframe, maxEndDate);
    }
    if (filters.mfa) {
        Payload.Filters.mfa = [filters.mfa];
    }
    if (filters.action) {
        Payload.Filters.action = [filters.action];
    }
    if (filters.directoryType) {
        Payload.Filters.directoryType = filters.directoryType;
    }
    if (filters.protocol) {
        Payload.Filters.protocol = [filters.protocol];
    }

    if (filters.authenticator) {
        Payload.Filters.authenticator = [filters.authenticator];
    }
    if (filters.includeSources?.length) {
        Payload.Filters.includeSources = filters.includeSources;
    }
    if (filters.includeSourcesIps?.length) {
        Payload.Filters.includeSourcesIps = filters.includeSourcesIps;
    }
    if (filters.includeSourceIpOrName?.length) {
        Payload.Filters.includeSourceIpOrName = filters.includeSourceIpOrName;
    }
    if (filters.includeDestinations?.length) {
        Payload.Filters.includeDestinations = filters.includeDestinations;
    }
    if (filters.includeDestinationsIps?.length) {
        Payload.Filters.includeDestinationsIps = filters.includeDestinationsIps;
    }
    if (filters.includeDestinationsIpOrName?.length) {
        Payload.Filters.includeDestinationsIpOrName = filters.includeDestinationsIpOrName;
    }
    if (filters.includeDestinationsApplications?.length) {
        Payload.Filters.includeDestinationsApplications = filters.includeDestinationsApplications;
    }
    if (filters.userType?.length) {
        Payload.Filters.userType = filters.userType;
    }
    if (filters.excludeUsers?.length) {
        Payload.Filters.excludeUsers = filters.excludeUsers;
    }

    return Payload;
};

const mutationFn = async (params: {
    filters: LogsFilters;
    excludedFilters: (keyof LogsFilters)[];
    maxEndDate?: Date;
}): Promise<ReturnType> => {
    const { filters, excludedFilters, maxEndDate } = params;
    const response = await apiPost<unknown, LogsResponse>(
        END_POINT,
        buildLogsPayload(filters, excludedFilters, maxEndDate)
    );
    const transformedData = convertLogEntryDtosToLogEntries(response.Logs);
    return {
        logs: transformedData,
        logsCount: response.LogsCount,
        defaultDestinations: response.defaultDestinations,
        wallNames: response.WallNames,
        hiddenRiskIndicators: response.hiddenRiskIndicators
    };
};

const useLogs = () => {
    const { mutate, error, isLoading, data } = useMutation<
        ReturnType,
        Error,
        { filters: LogsFilters; excludedFilters: (keyof LogsFilters)[]; maxEndDate?: Date }
    >({
        mutationFn: mutationFn
    });

    return {
        isLoading,
        error,
        mutate,
        data
    };
};

export default useLogs;
